// ABOUTME: CPO friction experiment (`no_enter_gate`, dev only) — the guest lands on the boutique with no Enter Boutique gate; the
// ABOUTME: mic CTA (first visit) or the enable-mic widget (return) is the entry, so consent, mic and agent audio follow a gesture.

import { useCallback, useEffect, useRef, useState } from 'react';
import { markIntroSeen } from '../state/visitMemory';

/**
 * Engages only where there is no film to protect: a returning guest (the reel boots on the collection) or a first-time guest
 * under `skip_intro_film`. A first-timer WITH the film keeps the gate — the film needs the gesture for its sound.
 *
 * @param {object} p
 * @param {Readonly<Record<string, true>>} p.ux the readUiExperiment() map
 * @param {boolean} p.reelMode this tenant is `reel_mode`
 * @param {boolean} p.skipFilm useIntroExperiment's skip_intro_film fact for this load
 * @param {boolean} p.hasSeenIntroBefore the read-once visit fact
 * @param {boolean} p.started App's isExperienceStarted (audio + agent initialised)
 * @param {Function} p.enter App's requestEnter, resolved at call time
 * @param {Function} [p.release] App's welcome funnel (sendIntroFinished), called once at entry
 * @returns {{ engaged: boolean, waiting: boolean, returning: boolean, isWaiting: Function, tap: Function, intercept: Function,
 *             flushHeld: Function }}
 *   `returning`: a returning guest's landing is entered from the enable-microphone widget (dots + controls already up), a
 *   first-timer's from the mic CTA
 */
export default function useLanding({ ux, reelMode, skipFilm, hasSeenIntroBefore, started, enter, release }) {
  const engaged = !!(ux.no_enter_gate && reelMode && (skipFilm || hasSeenIntroBefore));
  const [tapped, setTapped] = useState(false);
  const tappedRef = useRef(false);
  const enteredRef = useRef(false);
  const enterRef = useRef(enter);
  enterRef.current = enter;
  const releaseRef = useRef(release);
  releaseRef.current = release;

  // The visit record is written at ENTRY (the tap-to-talk rule: when the welcome is released) - not at the tap, so a guest who
  // taps, sees the consent gate and leaves is still a first-timer next time. A session that ends after entry (a 1008 auth close)
  // puts the landing back, never a boutique with no way in.
  useEffect(() => {
    if (started) {
      const entering = engaged && !enteredRef.current;
      if (entering && tappedRef.current) markIntroSeen();
      enteredRef.current = true;
      // The welcome trigger was held for the whole landing (App's funnel defers it while isWaiting()): release it now, once. With
      // the agent already connected (early connect) it goes out at once; otherwise it is banked for onopen's own flush.
      if (entering) releaseRef.current?.();
      return;
    }
    if (!enteredRef.current) return;
    enteredRef.current = false;
    tappedRef.current = false;
    setTapped(false);
  }, [started, engaged]);

  const waiting = engaged && !started && !tapped;
  // Until entry: the hero idle / collection boot's own intro report must not write the visit record either (useIntroExperiment).
  const isWaiting = useCallback(() => engaged && !enteredRef.current, [engaged]);

  // `enterOverride`: the widget's 'Turn on voice' enters by recording voice consent first (anything else, e.g. a click event, is ignored).
  const tap = useCallback((enterOverride) => {
    if (!engaged || tappedRef.current) return;
    tappedRef.current = true;
    setTapped(true);
    console.log('[UX][LANDING] tap -> enter');
    (typeof enterOverride === 'function' ? enterOverride : enterRef.current)?.();
  }, [engaged]);

  // Owner 2026-10-08: on a returning landing NOTHING starts the agent but 'Start conversation', the mic button, a watch label or a
  // sent message. Bag, wishlist, menu and empty space just work. An action that needs the agent (a label, a message, a menu chapter)
  // starts the conversation and is HELD until the welcome is triggered - sent right behind it, never waiting for it to finish (the
  // backend loads the collection at that trigger, so earlier it could not route the request).
  const heldRef = useRef([]);
  const intercept = useCallback((send) => {
    if (!engaged || enteredRef.current) return false;
    heldRef.current.push(send);
    tap();
    return true;
  }, [engaged, tap]);
  const flushHeld = useCallback(() => {
    const held = heldRef.current;
    heldRef.current = [];
    if (held.length) console.log(`[UX][LANDING] welcome triggered: sending held=${held.length}`);
    held.forEach((send) => { try { send(); } catch (e) { console.warn('[UX][LANDING] held request failed', e); } });
  }, []);
  const returning = engaged && hasSeenIntroBefore;

  return { engaged, waiting, returning, isWaiting, tap, intercept, flushHeld };
}
